import { AccountMenu } from './AccountMenu';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Divider,
  Stack,
  IconButton,
  ListItemDecorator,
} from '@mui/joy';
import {
  Buildings as BusinessOutlined,
  Lightning as FlashOnOutlined,
  UserPlus as PersonAdd,
  ClipboardText as Assignment,
  ClipboardText as Task,
  Briefcase as Work,
  ChatCircle as ChatOutlined,
  List as MenuOutlined,
  Moon as DarkModeOutlined,
  Sun as LightModeOutlined,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { useColorScheme } from '@mui/joy/styles';
import { useNavigate } from 'react-router-dom';
import { PageHelpButton } from '../knowledge-base/PageHelpButton';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { NotificationIcon } from '../NotificationIcon';
import { GlobalSearchDialog } from '../search/GlobalSearchDialog';

interface TopBarProps {
  sidebarWidth: number;
  onMobileMenuClick?: () => void;
  /** Pixels to push the top bar down for the impersonation bar. */
  topOffset?: number;
}

export const TopBar: React.FC<TopBarProps> = ({ sidebarWidth, onMobileMenuClick, topOffset = 0 }) => {
  const { user, logout } = useAuth();
  const { openPicker } = useImpersonation();
  const { mode, setMode } = useColorScheme();
  const resolvedMode = mode === 'dark' ? 'dark' : 'light';
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  // ⌘K / Ctrl+K opens Global Search from anywhere in the app.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // A rich-text editor that already used ⌘K (insert link) keeps it.
      if (event.defaultPrevented) return;
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '84px',
        px: { xs: 1.5, md: 3 },
        py: '10px',
        backgroundColor: '#5014b8',


        color: 'white',
        position: 'fixed',
        top: `${topOffset}px`,
        left: { xs: 0, md: sidebarWidth },
        right: 0,
        zIndex: 999,
        transition: 'left 0.2s ease',
      }}
    >
      {/* Mobile hamburger */}
      <IconButton
        variant="plain"
        onClick={onMobileMenuClick}
        aria-label="Open navigation"
        sx={{
          display: { xs: 'inline-flex', md: 'none' },
          color: 'white',
          minHeight: 40,
          minWidth: 40,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <MenuOutlined color="white" />
      </IconButton>

      <Box sx={{ flexShrink: 0, ml: { xs: 0, md: 0.5 } }}><PageHelpButton label inverse /></Box>

      {/* Global Search */}
      <Box
        component="button"
        type="button"
        onClick={() => setSearchOpen(true)}
        aria-label="Search"
        aria-keyshortcuts={isMac ? 'Meta+K' : 'Control+K'}
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          flex: '0 1 420px',
          minWidth: 200,
          height: 40,
          mx: 3,
          px: 1.5,
          border: '1px solid rgba(255, 255, 255, 0.24)',
          borderRadius: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          color: 'rgba(255, 255, 255, 0.85)',
          font: 'inherit',
          fontSize: '14px',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease, border-color 0.15s ease',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.18)', borderColor: 'rgba(255, 255, 255, 0.36)' },
          '&:focus-visible': { outline: '2px solid white', outlineOffset: '2px' },
        }}
      >
        <MagnifyingGlass size={18} color="white" />
        <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>Search…</Box>
        <Box
          component="kbd"
          sx={{
            fontFamily: 'inherit',
            fontSize: '12px',
            px: 0.75,
            py: 0.25,
            borderRadius: '6px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'rgba(255, 255, 255, 0.85)',
          }}
        >
          {isMac ? '⌘K' : 'Ctrl K'}
        </Box>
      </Box>
      <GlobalSearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      {/* Right side: Quick Actions, Notifications, User Menu */}
      <Stack direction="row" spacing={{ xs: 0.5, md: 1.5 }} alignItems="center" sx={{ ml: 'auto' }}>
        {/* Global Search (mobile) */}
        <IconButton
          variant="plain"
          onClick={() => setSearchOpen(true)}
          aria-label="Search"
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            color: 'white',
            minHeight: 40,
            minWidth: 40,
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <MagnifyingGlass color="white" />
        </IconButton>

        {/* Quick Actions */}
        <Dropdown>
          <MenuButton aria-label="Quick actions"
            variant="plain"
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              color: 'white',
              minHeight: '40px',
              minWidth: '40px',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <FlashOnOutlined color="white" />
          </MenuButton>
          <Menu sx={{ zIndex: 1200 }}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography level="h4" startDecorator={<FlashOnOutlined />}>
                Quick Actions
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => navigate('/crm/contacts')}>
              <ListItemDecorator><PersonAdd /></ListItemDecorator>
              Add Contact
            </MenuItem>
            <MenuItem onClick={() => navigate('/crm/pipeline')}>
              <ListItemDecorator><BusinessOutlined /></ListItemDecorator>
              Add Opportunity
            </MenuItem>
            <MenuItem onClick={() => navigate('/crm/contracts')}>
              <ListItemDecorator><Assignment /></ListItemDecorator>
              Add Contract
            </MenuItem>
            <MenuItem onClick={() => navigate('/operations/tasks')}>
              <ListItemDecorator><Task /></ListItemDecorator>
              Add Task
            </MenuItem>
            <MenuItem onClick={() => navigate('/operations/jobs')}>
              <ListItemDecorator><Work /></ListItemDecorator>
              Add Job
            </MenuItem>
          </Menu>
        </Dropdown>

        {/* Team Chat */}
        <IconButton
          variant="plain"
          onClick={() => navigate('/chat')}
          sx={{
            display: { xs: 'none', md: 'inline-flex' },
            color: 'white',
            minHeight: '40px',
            minWidth: '40px',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <ChatOutlined color="white" />
        </IconButton>

        {/* Light / Dark mode toggle */}
        <IconButton
          variant="plain"
          onClick={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')}
          aria-label="Toggle color mode"
          sx={{
            display: { xs: 'none', md: 'inline-flex' },
            color: 'white',
            minHeight: '40px',
            minWidth: '40px',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {resolvedMode === 'dark'
            ? <LightModeOutlined color="white" />
            : <DarkModeOutlined color="white" />}
        </IconButton>

        <NotificationIcon />

        <AccountMenu user={user} onLogout={handleLogout} onImpersonate={openPicker} />
      </Stack>
    </Box>
  );
};
